import React, { useState, useEffect, useCallback } from 'react';
import { Flower2, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Droplet, Heart, Sun, Moon, Info, Settings, Bell, History, BellRing, CloudOff } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import BottomNav from '../components/BottomNav';
import { cycleAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useOffline } from '../context/OfflineContext';

const Calendar = () => {
  const navigate = useNavigate();
  const { isOnline, cacheData, getCachedData } = useOffline();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [insights, setInsights] = useState([]);
  const [education, setEducation] = useState(null);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [cycleStats, setCycleStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isStaleData, setIsStaleData] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [reminderSettings, setReminderSettings] = useState({
    periodReminder: true,
    periodDaysBefore: 3,
    ovulationReminder: true,
    ovulationDaysBefore: 1,
    fertileWindowReminder: true
  });
  const [cycleSettings, setCycleSettings] = useState({
    cycleLength: 28,
    periodLength: 5,
    lastPeriodStart: new Date().toISOString().split('T')[0]
  });

  // Fetch reminders
  const fetchReminders = async () => {
    try {
      const response = await api.get('/cycle/reminders');
      setReminders(response.data.reminders || []);
      if (response.data.settings) {
        setReminderSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  // Fetch calendar data for specific month
  const fetchCalendarForMonth = useCallback(async (year, month) => {
    const cacheKey = `/api/cycle/month/${year}/${month}`;
    try {
      const response = await api.get(`/cycle/month/${year}/${month}`);
      setCalendarData(response.data.calendarData || []);
      if (response.data.cycleStats) {
        setCycleStats(response.data.cycleStats);
      }
      setIsStaleData(false);
      cacheData(cacheKey, response.data);
    } catch (error) {
      console.error('Error fetching calendar month:', error);
      // Try cached data
      const cached = await getCachedData(cacheKey);
      if (cached) {
        setCalendarData(cached.data.calendarData || []);
        if (cached.data.cycleStats) setCycleStats(cached.data.cycleStats);
        setIsStaleData(true);
      }
    }
  }, [cacheData, getCachedData]);

  const fetchAdvancedCycle = async () => {
    const cacheKey = '/api/cycle/advanced';
    try {
      const response = await api.get('/cycle/advanced');
      if (response.data.cycle) {
        setInsights(response.data.insights || []);
        setEducation(response.data.education);
        setCurrentPhase(response.data.currentPhase);
        setCycleStats(response.data.cycleStats);
        setCycleSettings({
          cycleLength: response.data.cycle.cycleLength || 28,
          periodLength: response.data.cycle.periodLength || 5,
          lastPeriodStart: response.data.cycle.lastPeriodStart || new Date().toISOString().split('T')[0]
        });
        cacheData(cacheKey, response.data);
        await fetchCalendarForMonth(currentDate.getFullYear(), currentDate.getMonth() + 1);
      } else {
        const today = new Date();
        const lastPeriodStart = today.toISOString().split('T')[0];
        await cycleAPI.create({
          cycleLength: 28,
          periodLength: 5,
          lastPeriodStart
        });
        const newResponse = await api.get('/cycle/advanced');
        setInsights(newResponse.data.insights || []);
        setEducation(newResponse.data.education);
        setCurrentPhase(newResponse.data.currentPhase);
        setCycleStats(newResponse.data.cycleStats);
        cacheData(cacheKey, newResponse.data);
        await fetchCalendarForMonth(currentDate.getFullYear(), currentDate.getMonth() + 1);
      }
    } catch (error) {
      console.error('Error fetching cycle:', error);
      // Try cached data when offline
      const cached = await getCachedData(cacheKey);
      if (cached) {
        setInsights(cached.data.insights || []);
        setEducation(cached.data.education);
        setCurrentPhase(cached.data.currentPhase);
        setCycleStats(cached.data.cycleStats);
        if (cached.data.cycle) {
          setCycleSettings({
            cycleLength: cached.data.cycle.cycleLength || 28,
            periodLength: cached.data.cycle.periodLength || 5,
            lastPeriodStart: cached.data.cycle.lastPeriodStart || new Date().toISOString().split('T')[0]
          });
        }
        setIsStaleData(true);
        // Load cached month data too
        const monthCacheKey = `/api/cycle/month/${currentDate.getFullYear()}/${currentDate.getMonth() + 1}`;
        const cachedMonth = await getCachedData(monthCacheKey);
        if (cachedMonth) {
          setCalendarData(cachedMonth.data.calendarData || []);
        }
      } else {
        toast({
          title: 'Erreur',
          description: isOnline ? 'Impossible de charger le calendrier' : 'Aucune donnée en cache disponible',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvancedCycle();
    fetchReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch new month data when navigating
  useEffect(() => {
    if (!loading) {
      fetchCalendarForMonth(currentDate.getFullYear(), currentDate.getMonth() + 1);
    }
  }, [currentDate, loading, fetchCalendarForMonth]);

  const updateReminderSettings = async () => {
    try {
      await api.put('/cycle/reminders/settings', reminderSettings);
      toast({
        title: 'Rappels mis à jour',
        description: 'Vos préférences de rappels ont été sauvegardées'
      });
      setShowReminders(false);
      fetchReminders();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour les rappels',
        variant: 'destructive'
      });
    }
  };

  const updateCycleSettings = async () => {
    try {
      await cycleAPI.update(cycleSettings);
      toast({
        title: 'Paramètres mis à jour',
        description: 'Votre cycle a été mis à jour avec succès'
      });
      setShowSettings(false);
      fetchAdvancedCycle();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour les paramètres',
        variant: 'destructive'
      });
    }
  };

  const setupReminders = () => {
    setShowReminders(true);
  };

  const getCycleColor = (type) => {
    switch (type) {
      case 'menstruation':
        return 'bg-red-500/80';
      case 'ovulation':
        return 'bg-pink-400/80';
      case 'fertile':
        return 'bg-green-500/80';
      case 'follicular':
        return 'bg-blue-400/80';
      case 'luteal':
        return 'bg-purple-500/80';
      case 'prediction':
        return 'bg-pink-300/50 border-2 border-dashed border-pink-400';
      default:
        return 'bg-purple-700/50';
    }
  };

  const getCycleIcon = (type) => {
    switch (type) {
      case 'menstruation':
        return <Droplet className="w-3 h-3 text-white" />;
      case 'ovulation':
        return <Sun className="w-3 h-3 text-yellow-300" />;
      case 'fertile':
        return <Heart className="w-3 h-3 text-green-300" />;
      case 'follicular':
        return <Moon className="w-3 h-3 text-blue-300" />;
      case 'luteal':
        return <Moon className="w-3 h-3 text-purple-300" />;
      default:
        return null;
    }
  };

  const getPhaseColor = (phase) => {
    const colors = {
      menstruation: 'from-red-500 to-red-700',
      follicular: 'from-blue-400 to-blue-600',
      ovulation: 'from-green-400 to-green-600',
      luteal: 'from-purple-500 to-purple-700'
    };
    return colors[phase] || 'from-pink-400 to-pink-600';
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Offline Banner */}
      {isStaleData && (
        <div className="bg-orange-600/90 px-4 py-2 flex items-center justify-center gap-2 text-white text-sm" data-testid="calendar-offline-banner">
          <CloudOff className="w-4 h-4" />
          <span>Données en cache{!isOnline ? ' (hors ligne)' : ' - mise à jour en cours'}</span>
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flower2 className="w-8 h-8 text-pink-300" />
            <h1 className="text-2xl font-bold text-white">Calendrier</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/cycle-history')}
              className="text-white hover:bg-white/10"
              title="Historique des cycles"
            >
              <History className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={setupReminders}
              className="text-white hover:bg-white/10"
            >
              <Bell className="w-5 h-5" />
            </Button>
            <Dialog open={showSettings} onOpenChange={setShowSettings}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                  <Settings className="w-5 h-5" />
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-purple-900 border-purple-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white text-xl flex items-center gap-2">
                    <CalendarIcon className="w-6 h-6 text-pink-400" />
                    Paramètres du cycle menstruel
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-5 py-4">
                  {/* Info box */}
                  <div className="p-4 bg-blue-500/20 rounded-lg border border-blue-400/30">
                    <p className="text-blue-200 text-sm">
                      Ces informations permettent de prédire vos prochaines règles, votre période d'ovulation et vos jours fertiles.
                    </p>
                  </div>

                  <div>
                    <Label className="text-pink-200 font-medium flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-pink-500/30 flex items-center justify-center text-xs">1</span>
                      Durée moyenne de votre cycle
                    </Label>
                    <p className="text-pink-300/70 text-xs mb-2 ml-8">Du 1er jour des règles au jour avant les règles suivantes</p>
                    <div className="flex items-center gap-3 ml-8">
                      <Input
                        type="number"
                        min="21"
                        max="40"
                        value={cycleSettings.cycleLength}
                        onChange={(e) => setCycleSettings({...cycleSettings, cycleLength: parseInt(e.target.value)})}
                        className="bg-white/10 border-white/20 text-white text-center text-lg font-bold w-20"
                      />
                      <span className="text-white">jours</span>
                      <span className="text-pink-300/60 text-xs">(moyenne: 28 jours)</span>
                    </div>
                  </div>

                  <div>
                    <Label className="text-pink-200 font-medium flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-red-500/30 flex items-center justify-center text-xs">2</span>
                      Durée de vos règles
                    </Label>
                    <p className="text-pink-300/70 text-xs mb-2 ml-8">Nombre de jours de saignement</p>
                    <div className="flex items-center gap-3 ml-8">
                      <Input
                        type="number"
                        min="2"
                        max="10"
                        value={cycleSettings.periodLength}
                        onChange={(e) => setCycleSettings({...cycleSettings, periodLength: parseInt(e.target.value)})}
                        className="bg-white/10 border-white/20 text-white text-center text-lg font-bold w-20"
                      />
                      <span className="text-white">jours</span>
                      <span className="text-pink-300/60 text-xs">(moyenne: 5 jours)</span>
                    </div>
                  </div>

                  <div>
                    <Label className="text-pink-200 font-medium flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-purple-500/30 flex items-center justify-center text-xs">3</span>
                      Début de vos dernières règles
                    </Label>
                    <p className="text-pink-300/70 text-xs mb-2 ml-8">Le premier jour de vos dernières règles</p>
                    <Input
                      type="date"
                      value={cycleSettings.lastPeriodStart}
                      onChange={(e) => setCycleSettings({...cycleSettings, lastPeriodStart: e.target.value})}
                      className="bg-white/10 border-white/20 text-white ml-8 w-auto"
                    />
                  </div>

                  <Button onClick={updateCycleSettings} className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-lg py-6 font-bold">
                    Enregistrer mes paramètres
                  </Button>

                  <p className="text-center text-pink-300/60 text-xs">
                    Vos données sont confidentielles et stockées de manière sécurisée.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Cycle Setup Banner - Show if cycle not configured or first visit */}
        {!cycleStats && (
          <Card className="p-6 bg-gradient-to-r from-pink-600 to-purple-600 border-0 shadow-xl">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
                <CalendarIcon className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Configurez votre cycle</h2>
              <p className="text-white/80 text-sm mb-4">
                Pour des prédictions précises de vos règles et ovulation, entrez les informations de votre cycle.
              </p>
              <Dialog open={showSettings} onOpenChange={setShowSettings}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-purple-600 hover:bg-white/90 font-bold px-8 py-3 text-lg">
                    Commencer la configuration
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>
          </Card>
        )}

        {/* Current Phase Card */}
        {currentPhase && education && (
          <Card className={`p-5 bg-gradient-to-r ${getPhaseColor(currentPhase.phase)} border-0`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                {getCycleIcon(currentPhase.type)}
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">{education.title}</h2>
                <p className="text-white/80 text-sm">Jour {currentPhase.day} du cycle</p>
              </div>
            </div>
            <p className="text-white/90 text-sm">{education.description}</p>
            {currentPhase.fertility && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-white/80 text-sm">Fertilité:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  currentPhase.fertility === 'peak' ? 'bg-green-400 text-green-900' :
                  currentPhase.fertility === 'high' ? 'bg-yellow-400 text-yellow-900' :
                  'bg-gray-300 text-gray-700'
                }`}>
                  {currentPhase.fertility === 'peak' ? 'Maximum' : 
                   currentPhase.fertility === 'high' ? 'Élevée' : 'Faible'}
                </span>
              </div>
            )}
          </Card>
        )}

        {/* Cycle Summary Card - when configured */}
        {cycleStats && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Info className="w-5 h-5 text-pink-400" />
                Mon cycle
              </h3>
              <Dialog open={showSettings} onOpenChange={setShowSettings}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-pink-300 hover:text-white hover:bg-white/10">
                    <Settings className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-pink-500/20 rounded-lg">
                <p className="text-2xl font-bold text-white">{cycleSettings.cycleLength}</p>
                <p className="text-pink-200 text-xs">Durée cycle</p>
              </div>
              <div className="text-center p-3 bg-red-500/20 rounded-lg">
                <p className="text-2xl font-bold text-white">{cycleSettings.periodLength}</p>
                <p className="text-pink-200 text-xs">Jours règles</p>
              </div>
              <div className="text-center p-3 bg-purple-500/20 rounded-lg">
                <p className="text-2xl font-bold text-white">{cycleStats?.cyclesTracked || 1}</p>
                <p className="text-pink-200 text-xs">Cycles suivis</p>
              </div>
            </div>
          </Card>
        )}

        {/* Month Navigation */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigateMonth(-1)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <h2 className="text-xl font-bold text-white">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button 
              onClick={() => navigateMonth(1)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </div>
        </Card>

        {/* Calendar Grid */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, i) => (
              <div key={`cal-header-${i}`} className="text-center text-pink-300 text-xs font-medium py-1">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calendarData.slice(0, 42).map((day, index) => {
              // Get today's date in local timezone (YYYY-MM-DD format)
              const now = new Date();
              const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
              const isToday = day.date === todayStr;
              
              return (
                <div
                  key={day.date || `day-${index}`}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center relative ${
                    day.type === 'empty' ? 'bg-transparent' : getCycleColor(day.type)
                  } ${
                    isToday ? 'ring-2 ring-yellow-400' : ''
                  } ${
                    day.isPrediction ? 'opacity-80' : ''
                  }`}
                >
                  {day.day && (
                    <>
                      <span className={`font-semibold text-xs ${day.type === 'empty' ? 'text-pink-300/30' : 'text-white'}`}>
                        {day.day}
                      </span>
                      {day.type !== 'empty' && day.type !== 'normal' && (
                        <span className="mt-0.5">{getCycleIcon(day.type)}</span>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex flex-wrap gap-3 justify-center text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-500/80"></div>
                <span className="text-pink-200">Règles</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-green-500/80"></div>
                <span className="text-pink-200">Fertile</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-pink-400/80"></div>
                <span className="text-pink-200">Ovulation</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-blue-400/80"></div>
                <span className="text-pink-200">Folliculaire</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-purple-500/80"></div>
                <span className="text-pink-200">Lutéale</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Insights */}
        {insights.length > 0 && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-pink-300" />
              <h3 className="text-lg font-bold text-white">Prédictions</h3>
            </div>
            <div className="space-y-3">
              {insights.map((insight) => (
                <div key={insight.title || insight.message} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                  <span className="text-2xl">{insight.icon}</span>
                  <div>
                    <h4 className="text-white font-medium text-sm">{insight.title}</h4>
                    <p className="text-pink-200 text-xs">{insight.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Tips */}
        {education && education.tips && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-pink-300" />
              <h3 className="text-lg font-bold text-white">Conseils</h3>
            </div>
            <ul className="space-y-2">
              {education.tips.map((tip, index) => (
                <li key={`tip-${index}-${tip.slice(0,20)}`} className="flex items-start gap-2 text-pink-100 text-sm">
                  <span className="text-pink-400 mt-1">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Legend */}
        <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
          <div className="flex items-center gap-2 mb-4">
            <CalendarIcon className="w-5 h-5 text-pink-300" />
            <h3 className="text-lg font-bold text-white">Légende</h3>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-red-500/80 flex items-center justify-center">
                <Droplet className="w-3 h-3 text-white" />
              </div>
              <span className="text-white text-sm">Menstruation</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-green-500/80 flex items-center justify-center">
                <Heart className="w-3 h-3 text-white" />
              </div>
              <span className="text-white text-sm">Période fertile</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-pink-400/80 flex items-center justify-center">
                <Sun className="w-3 h-3 text-yellow-300" />
              </div>
              <span className="text-white text-sm">Ovulation</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-blue-400/80 flex items-center justify-center">
                <Moon className="w-3 h-3 text-white" />
              </div>
              <span className="text-white text-sm">Phase folliculaire</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-purple-500/80 flex items-center justify-center">
                <Moon className="w-3 h-3 text-white" />
              </div>
              <span className="text-white text-sm">Phase lutéale</span>
            </div>
          </div>
        </Card>

        {/* Cycle Stats */}
        {cycleStats && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <h3 className="text-lg font-bold text-white mb-3">Statistiques</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 rounded-lg bg-white/5">
                <p className="text-2xl font-bold text-pink-300">{cycleStats.cycleLength}</p>
                <p className="text-xs text-pink-200">Durée du cycle</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/5">
                <p className="text-2xl font-bold text-pink-300">{cycleStats.periodLength}</p>
                <p className="text-xs text-pink-200">Jours de règles</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/5">
                <p className="text-2xl font-bold text-green-400">J{cycleStats.ovulationDay}</p>
                <p className="text-xs text-pink-200">Ovulation</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/5">
                <p className="text-2xl font-bold text-green-400">J{cycleStats.fertileWindowStart}-{cycleStats.fertileWindowEnd}</p>
                <p className="text-xs text-pink-200">Fenêtre fertile</p>
              </div>
            </div>
          </Card>
        )}

        {/* Upcoming Reminders */}
        {reminders.length > 0 && (
          <Card className="p-4 bg-gradient-to-r from-pink-500/20 to-purple-500/20 backdrop-blur-md border-pink-400/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BellRing className="w-5 h-5 text-pink-300" />
                <h3 className="text-lg font-bold text-white">Rappels à venir</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReminders(true)}
                className="text-pink-300 hover:text-white"
              >
                Configurer
              </Button>
            </div>
            <div className="space-y-3">
              {reminders.map((reminder, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    reminder.priority === 'high' 
                      ? 'bg-red-500/20 border border-red-400/30' 
                      : reminder.priority === 'medium'
                      ? 'bg-yellow-500/20 border border-yellow-400/30'
                      : 'bg-white/10'
                  }`}
                >
                  <span className="text-2xl">{reminder.icon}</span>
                  <div className="flex-1">
                    <h4 className="text-white font-medium text-sm">{reminder.title}</h4>
                    <p className="text-pink-200 text-xs">{reminder.message}</p>
                  </div>
                  {reminder.daysUntil === 0 && (
                    <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                      Aujourd'hui
                    </span>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Reminders Settings Dialog */}
      <Dialog open={showReminders} onOpenChange={setShowReminders}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <BellRing className="w-6 h-6 text-pink-400" />
              Configurer les rappels
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="p-4 bg-pink-500/20 rounded-lg border border-pink-400/30">
              <p className="text-pink-200 text-sm">
                Recevez des rappels pour anticiper vos règles, votre ovulation et votre fenêtre fertile.
              </p>
            </div>

            {/* Period Reminder */}
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🩸</span>
                <div>
                  <p className="text-white font-medium">Rappel des règles</p>
                  <p className="text-pink-200 text-xs">Avant le début de vos règles</p>
                </div>
              </div>
              <Switch
                checked={reminderSettings.periodReminder}
                onCheckedChange={(checked) => setReminderSettings({...reminderSettings, periodReminder: checked})}
              />
            </div>
            
            {reminderSettings.periodReminder && (
              <div className="ml-4 flex items-center gap-3">
                <span className="text-pink-200 text-sm">Me rappeler</span>
                <Input
                  type="number"
                  min="1"
                  max="7"
                  value={reminderSettings.periodDaysBefore}
                  onChange={(e) => setReminderSettings({...reminderSettings, periodDaysBefore: parseInt(e.target.value)})}
                  className="bg-white/10 border-white/20 text-white text-center w-16"
                />
                <span className="text-pink-200 text-sm">jours avant</span>
              </div>
            )}

            {/* Ovulation Reminder */}
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🌸</span>
                <div>
                  <p className="text-white font-medium">Rappel d'ovulation</p>
                  <p className="text-pink-200 text-xs">Avant votre jour d'ovulation</p>
                </div>
              </div>
              <Switch
                checked={reminderSettings.ovulationReminder}
                onCheckedChange={(checked) => setReminderSettings({...reminderSettings, ovulationReminder: checked})}
              />
            </div>
            
            {reminderSettings.ovulationReminder && (
              <div className="ml-4 flex items-center gap-3">
                <span className="text-pink-200 text-sm">Me rappeler</span>
                <Input
                  type="number"
                  min="1"
                  max="3"
                  value={reminderSettings.ovulationDaysBefore}
                  onChange={(e) => setReminderSettings({...reminderSettings, ovulationDaysBefore: parseInt(e.target.value)})}
                  className="bg-white/10 border-white/20 text-white text-center w-16"
                />
                <span className="text-pink-200 text-sm">jours avant</span>
              </div>
            )}

            {/* Fertile Window Reminder */}
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">💚</span>
                <div>
                  <p className="text-white font-medium">Fenêtre fertile</p>
                  <p className="text-pink-200 text-xs">Au début de votre période fertile</p>
                </div>
              </div>
              <Switch
                checked={reminderSettings.fertileWindowReminder}
                onCheckedChange={(checked) => setReminderSettings({...reminderSettings, fertileWindowReminder: checked})}
              />
            </div>

            {/* Push Notification Info */}
            <div className="p-4 bg-gradient-to-r from-green-500/20 to-teal-500/20 rounded-lg border border-green-400/30">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-5 h-5 text-green-400" />
                <p className="text-white font-medium">Notifications Push</p>
              </div>
              <p className="text-green-200 text-sm">
                Les rappels seront envoyés automatiquement sur votre téléphone aux dates prévues. 
                Assurez-vous d'avoir activé les notifications dans les paramètres de l'application.
              </p>
            </div>

            <Button
              onClick={updateReminderSettings}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
            >
              Sauvegarder les préférences
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav active="calendar" />
    </div>
  );
};

export default Calendar;
